import { EaVideoPlayerKeybindings, IEaVideoPlayerSkipButtonConfig, IEaVideoPlayerStateModel, IEaVideoPlayerTimeSkipUnit } from './ea-video-player.models';

import { IStateSliceDefaults, StateSliceName } from 'src/app/states/store.models';
import { createHotkeyBinding } from 'src/app/services/hotkey/hotkey.utils';
import { HotkeyEventType, IHotkeyBinding } from 'src/app/services/hotkey/hotkey.models';
import { ClockImpactType, IGameEvent } from 'src/app/states/event/event.models';

export const VIDEO_SPEED_FORM_STATE_KEY = 'videoSpeedForm';

export const DEFAULT_VIDEO_SPEED = 1;

/** Seek amount in seconds per wheel step when holding Shift and scrolling over the video */
export const SHIFT_SCROLL_SEEK_SECONDS = 1;

/** Throttle interval (ms) for shift+scroll seek to avoid excessive seeks on trackpads */
export const SHIFT_SCROLL_SEEK_THROTTLE_MS = 30;

/** Hotkey bindings for the video player */
export const VIDEO_PLAYER_HOTKEYS: IHotkeyBinding[] = [
    createHotkeyBinding(EaVideoPlayerKeybindings.MUTE, 'M', HotkeyEventType.KEYUP),
    createHotkeyBinding(EaVideoPlayerKeybindings.PLAY, 'Space', HotkeyEventType.KEYDOWN),
    createHotkeyBinding(EaVideoPlayerKeybindings.PAUSE, 'Space', HotkeyEventType.KEYUP),
    createHotkeyBinding(EaVideoPlayerKeybindings.SPEED_1, '1', HotkeyEventType.KEYUP),
    createHotkeyBinding(EaVideoPlayerKeybindings.SPEED_2, '2', HotkeyEventType.KEYUP),
    createHotkeyBinding(EaVideoPlayerKeybindings.SPEED_3, '3', HotkeyEventType.KEYUP),
    createHotkeyBinding(EaVideoPlayerKeybindings.SPEED_4, '4', HotkeyEventType.KEYUP),
    createHotkeyBinding(EaVideoPlayerKeybindings.SPEED_5, '5', HotkeyEventType.KEYUP),
    createHotkeyBinding(EaVideoPlayerKeybindings.FORWARD_1_FRAME, 'ArrowRight', HotkeyEventType.KEYUP),
    createHotkeyBinding(EaVideoPlayerKeybindings.FORWARD_1_SECOND, 'ArrowUp', HotkeyEventType.KEYUP),
    createHotkeyBinding(EaVideoPlayerKeybindings.BACKWARD_1_FRAME, 'ArrowLeft', HotkeyEventType.KEYUP),
    createHotkeyBinding(EaVideoPlayerKeybindings.BACKWARD_1_SECOND, 'ArrowDown', HotkeyEventType.KEYUP)
];

export const eaVideoPlayerStateDefaults: IStateSliceDefaults<IEaVideoPlayerStateModel> = Object.freeze({
    sliceName: StateSliceName.EA_VIDEO_PLAYER,
    defaults: {
        queryParamsTimestamp: undefined,
        queryParamsEventId: undefined,
        videoCanBePlayed: false,
        videoMuted: true,
        videoPlaying: false,
        [VIDEO_SPEED_FORM_STATE_KEY]: {
            model: {
                videoSpeed: DEFAULT_VIDEO_SPEED,
            },
            dirty: false,
            status: 'VALID',
            errors: {},
        },
        videoPlaybackError: undefined,
        currentVideoTimeSeconds: 0
    }
});

export const VIDEO_SPEED_OPTIONS: {
    label: string;
    value: number;
    hotkey: string;
}[] = [
    {
        label: '0.25x',
        value: 0.25,
        hotkey: '1'
    },
    {
        label: '1.0x',
        value: DEFAULT_VIDEO_SPEED,
        hotkey: '2'
    },
    {
        label: '1.5x',
        value: 1.5,
        hotkey: '3'
    },
    {
        label: '1.8x',
        value: 1.8,
        hotkey: '4'
    },
    {
        label: '4x',
        value: 4,
        hotkey: '5'
    },
];

export const VIDEO_PLAYER_TIME_SKIP_BUTTONS: IEaVideoPlayerSkipButtonConfig[] = [
    {
        translationString: 'VIDEO_PLAYER.MINUTES_ADJUSTMENT',
        timeLabel: '1',
        timeAmount: 60,
        timeUnits: IEaVideoPlayerTimeSkipUnit.SECONDS
    },
    {
        translationString: 'VIDEO_PLAYER.SECONDS_ADJUSTMENT',
        timeLabel: '15',
        timeAmount: 15,
        timeUnits: IEaVideoPlayerTimeSkipUnit.SECONDS
    },
    {
        translationString: 'VIDEO_PLAYER.SECONDS_ADJUSTMENT',
        timeLabel: '1',
        timeAmount: 1,
        timeUnits: IEaVideoPlayerTimeSkipUnit.SECONDS
    },
    {
        translationString: 'VIDEO_PLAYER.FRAMES_ADJUSTMENT',
        timeLabel: '1',
        timeAmount: 1,
        timeUnits: IEaVideoPlayerTimeSkipUnit.FRAMES
    }
];

export const HLS_CREDENTIALS = {
    debug: false,
    maxFragLookUpTolerance: 0,
    autoStartLoad: false,
    enableCEA708Captions: false,
    nudgeMaxRetry: 10,
    nudgeOffset: 0.4,
    xhrSetup: (xhr: XMLHttpRequest): void => {
        // sends the cookies
        xhr.withCredentials = true;
    },
};

/**
 * Converts a string timestamp in the format HH:MM:SS:FF (where FF is frames)
 * and a framerate into a total seconds value.
 *
 * @param timestamp The time string (HH:MM:SS:FF).
 * @param framerate The number of frames per second
 * @returns The total time in seconds
 */
export const convertStringTimestampToSeconds = (timestamp: string, framerate: number): number => {
    if (!timestamp || framerate <= 0) {
        throw new Error('Invalid timestamp or framerate.');
    }

    const parts = timestamp.split(':');

    if (parts.length !== 4) {
        throw new Error(`Timestamp format must be HH:MM:SS:FF. Received: ${timestamp}`);
    }

    const [hours, minutes, seconds, frames] = parts.map(part => parseInt(part, 10));
    const totalSeconds = (hours * 3600) + (minutes * 60) + seconds;
    const framesInSeconds = convertFramesToSeconds(frames, framerate);
    return totalSeconds + framesInSeconds;
};

/**
 * Takes a number of seconds and converts it to a string with the format HH:mm:ss or HH:mm:ss:ff
 * @param seconds
 * @returns
 */
export const convertSecondsToStringTimestamp = (
    seconds: number,
    includeFrames = false,
    framerate?: number
// eslint-disable-next-line complexity
): string => {
    if (isNaN(seconds) || seconds < 0) {
        // If frames are included, use 00:00:00:00
        return includeFrames ? '00:00:00:00' : '00:00:00';
    }

    let integerSeconds = Math.floor(seconds);
    const fractionalSeconds = seconds - integerSeconds;

    const shouldIncludeFrames = includeFrames && !!framerate && framerate > 0;
    let frames = 0;

    if (shouldIncludeFrames) {
        // Calculate the number of frames from the fractional part of a second.
        // Math.round is used here because the fractionalSeconds may not be perfectly precise
        frames = Math.round(fractionalSeconds * framerate);

        // Handle edge case where rounding pushes frames to the next second (e.g., 29.999/30fps
        // rounds up to frame 30): carry into the seconds instead of wrapping with `%`, which
        // would produce a timestamp almost a full second before the actual position (and a
        // non-integer frame count for fractional framerates such as 29.97).
        if (frames >= framerate) {
            frames = 0;
            integerSeconds += 1;
        }
    }

    const hours = Math.floor(integerSeconds / 3600);
    const remainingSecondsAfterHours = integerSeconds % 3600;
    const minutes = Math.floor(remainingSecondsAfterHours / 60);
    const ss = remainingSecondsAfterHours % 60; // ss is now an integer

    const hh = String(hours).padStart(2, '0');
    const mm = String(minutes).padStart(2, '0');
    const stringSeconds = String(ss).padStart(2, '0');

    if (shouldIncludeFrames) {
        const ff = String(frames).padStart(2, '0');
        return `${hh}:${mm}:${stringSeconds}:${ff}`;
    }
    return `${hh}:${mm}:${stringSeconds}`;
};

/**
 * Converts the given amount of frames into seconds based on the passed framerate
 * @param frame
 * @param frameRate
 */
export const convertFramesToSeconds = (frames: number, frameRate: number): number => {
    return frames / frameRate;
};

/**
 * Converts a string timestamp (HH:MM:SS:FF) into an absolute frame count for the given framerate.
 *
 * @param timestamp The time string (HH:MM:SS:FF).
 * @param framerate The number of frames per second.
 * @returns The absolute frame number.
 */
export const convertStringTimestampToFrame = (timestamp: string, framerate: number): number => {
    const seconds = convertStringTimestampToSeconds(timestamp, framerate);
    return Math.round(seconds * framerate);
};

/**
 * Converts an absolute frame count into a string timestamp (HH:MM:SS:FF) for the given framerate.
 * This is the inverse of {@link convertStringTimestampToFrame}.
 *
 * @param frame The absolute frame count.
 * @param framerate The number of frames per second.
 * @returns The timestamp string in the format HH:MM:SS:FF.
 */
export const convertFrameToStringTimestamp = (frame: number, framerate: number): string => {
    const seconds = convertFramesToSeconds(frame, framerate);
    return convertSecondsToStringTimestamp(seconds, true, framerate);
};

/**
 * Formats a duration in seconds as a period clock string `M:SS.cc`, where `cc`
 * is hundredths of a second.
 *
 * Returns `undefined` while the period's game events have not been loaded yet, so callers can wait
 * for the events fetch to complete before seeking.
 *
 * @param frameRate The frame rate of the period's video
 * @param periodNumber The selected period
 * @param gameEvents All loaded game events
 * @param clockImpactByDefId Map of game-event definition id to its clock impact
 */
export const computePeriodStartVideoSeconds = (
    frameRate: number,
    periodNumber: number,
    gameEvents: IGameEvent[],
    clockImpactByDefId: Record<string, ClockImpactType>
// eslint-disable-next-line complexity
): number | undefined => {
    if (frameRate <= 0) {
        return undefined;
    }

    let earliestEvent: IGameEvent | undefined;
    let earliestStartEvent: IGameEvent | undefined;

    for (const event of gameEvents) {
        if (Number(event.period) !== Number(periodNumber)) {
            continue;
        }

        if (!earliestEvent || event.frame < earliestEvent.frame) {
            earliestEvent = event;
        }

        const isStartEvent = clockImpactByDefId[event.defId] === ClockImpactType.start;
        if (isStartEvent && (!earliestStartEvent || event.frame < earliestStartEvent.frame)) {
            earliestStartEvent = event;
        }
    }

    const anchor = earliestStartEvent ?? earliestEvent;
    return anchor ? anchor.frame / frameRate : undefined;
};
